import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

const FONT_SANS = "'Geist', 'SF Pro Display', -apple-system, sans-serif";
const FONT_MONO = "'Geist Mono', 'SF Mono', monospace";
const API = '/api/chamas';

export default function ChamaVotes({ chamaId, onBack }) {
  const { T } = useTheme();
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ market_id: '', outcome: '', option_id: '', proposed_amount: '', description: '', duration_hours: 24 });
  const [marketSearch, setMarketSearch] = useState('');
 const [markets, setMarkets] = useState([]);
const [allMarkets, setAllMarkets] = useState([]);
  const [proposeLoading, setProposeLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [marketDetail, setMarketDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => { fetchVotes(); }, [chamaId, tab]);

  const fetchVotes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/${chamaId}/votes?status=${tab}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setVotes(d.votes || []);
    } finally { setLoading(false); }
  };

  // Fetch all open markets once when form opens
useEffect(() => {
  if (!showForm) return;
  fetch(`/api/markets?status=open`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.json())
    .then(d => setAllMarkets(d.markets || d || []))
    .catch(() => setAllMarkets([]));
}, [showForm]);

// Filter client-side as user types
useEffect(() => {
  if (!marketSearch.trim()) { setMarkets([]); return; }
  const q = marketSearch.toLowerCase();
  setMarkets(allMarkets.filter(m => m.title?.toLowerCase().includes(q) || m.category?.toLowerCase().includes(q)));
}, [marketSearch, allMarkets]);

  const selectMarket = async (m) => {
    setSelectedMarket(m);
    setForm(f => ({ ...f, market_id: m.id, outcome: '', option_id: '' }));
    setMarketSearch('');
    setMarkets([]);
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/markets/${m.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setMarketDetail(d);
    } catch { setMarketDetail(null); }
    finally { setDetailLoading(false); }
  };

  const clearMarket = () => {
    setSelectedMarket(null);
    setMarketDetail(null);
    setForm(f => ({ ...f, market_id: '', outcome: '', option_id: '' }));
    setMarketSearch('');
    setMarkets([]);
  };

  const proposeVote = async () => {
    if (!form.market_id) return setMsg({ type: 'error', text: 'Select a market' });
    if (!form.outcome) return setMsg({ type: 'error', text: 'Select an outcome' });
    if (!form.proposed_amount || parseFloat(form.proposed_amount) < 10) return setMsg({ type: 'error', text: 'Minimum amount is KES 10' });
    setProposeLoading(true); setMsg(null);
    try {
      const res = await fetch(`${API}/${chamaId}/votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, market_id: parseInt(form.market_id), proposed_amount: parseFloat(form.proposed_amount) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMsg({ type: 'success', text: 'Vote proposal created' });
      setShowForm(false);
      setSelectedMarket(null);
      setMarketDetail(null);
      setForm({ market_id: '', outcome: '', option_id: '', proposed_amount: '', description: '', duration_hours: 24 });
      setMarketSearch('');
      setMarkets([]);
      await fetchVotes();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setProposeLoading(false); }
  };

  const castVote = async (voteId, vote) => {
    try {
      const res = await fetch(`${API}/${chamaId}/votes/${voteId}/cast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ vote }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchVotes();
    } catch (e) { alert(e.message); }
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 };

  // Derive outcome options from market detail
  const getOutcomeOptions = () => {
    if (!marketDetail) return null;
    if (marketDetail.market_type === 'multiple') {
      return { type: 'multi', options: marketDetail.options || [] };
    }
    return {
      type: 'binary',
      yes: marketDetail.yes_label || 'YES',
      no: marketDetail.no_label || 'NO',
    };
  };

  const outcomeOptions = getOutcomeOptions();

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>

      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px',
        background: `${T.bg}ee`, backdropFilter: 'blur(16px)', borderBottom: `1px solid ${T.border}`,
      }}>
        <button onClick={onBack} style={{
          fontSize: 13, color: T.textMuted, padding: '6px 14px', borderRadius: 8,
          border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer', fontFamily: FONT_SANS,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>← Back</button>
        <button onClick={() => { setShowForm(!showForm); setMsg(null); }} style={{
          padding: '8px 18px', borderRadius: 8, border: 'none', background: T.accent,
          color: '#fff', fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>{showForm ? 'Cancel' : '+ Propose Vote'}</button>
      </nav>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px 80px', animation: 'fadeIn 0.4s ease' }}>

        <div style={{ marginBottom: 28 }}>
          <h1 style={{ color: T.text, fontSize: 22, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.02em' }}>Trade Votes</h1>
          <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>Propose and vote on chama trades</p>
        </div>

        {/* Propose form */}
        {showForm && (
          <div style={{ ...card, padding: '24px', marginBottom: 20 }}>
            <h3 style={{ color: T.text, fontSize: 15, fontWeight: 600, margin: '0 0 20px' }}>Propose Trade Vote</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Market search */}
              <FormField label="Market" T={T}>
                {selectedMarket ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: T.surfaceHi, border: `1px solid ${T.border}`,
                    borderRadius: 10, padding: '11px 14px',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: T.textDim, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Selected</div>
                      <div style={{ color: T.text, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedMarket.title}</div>
                      {selectedMarket.category && (
                        <div style={{ color: T.textDim, fontSize: 11, marginTop: 2 }}>{selectedMarket.category}</div>
                      )}
                    </div>
                    <button onClick={clearMarket} style={{
                      background: 'none', border: `1px solid ${T.border}`, borderRadius: 6,
                      color: T.textMuted, cursor: 'pointer', fontSize: 13, padding: '4px 10px',
                      fontFamily: FONT_SANS, flexShrink: 0, marginLeft: 10,
                    }}>Change</button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input
                      value={marketSearch}
                      onChange={e => { setMarketSearch(e.target.value); setForm(f => ({ ...f, market_id: '' })); }}
                      placeholder="Search open markets..."
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        background: T.surfaceHi, border: `1px solid ${T.border}`,
                        borderRadius: 10, padding: '11px 14px',
                        color: T.text, fontFamily: FONT_SANS, fontSize: 13, outline: 'none',
                      }}
                    />
                    {markets.length > 0 && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
                        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
                        overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                      }}>
                        {markets.slice(0, 6).map((m, i) => (
                          <div key={m.id} onClick={() => selectMarket(m)}
                            style={{
                              padding: '10px 14px', cursor: 'pointer',
                              borderBottom: i < Math.min(markets.length, 6) - 1 ? `1px solid ${T.border}` : 'none',
                              color: T.text, fontSize: 13, transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <div style={{ fontWeight: 500, marginBottom: 2 }}>{m.title}</div>
                            <div style={{ fontSize: 11, color: T.textDim }}>
                              {m.category}{m.market_type === 'multiple' ? ' · Multi-choice' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {marketSearch.trim() && markets.length === 0 && (
                      <div style={{
                        marginTop: 4, padding: '10px 14px', color: T.textDim, fontSize: 13,
                        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
                      }}>
                        No open markets found
                      </div>
                    )}
                  </div>
                )}
              </FormField>

              {/* Outcome — only shown after market selected */}
              {selectedMarket && (
                <FormField label="Outcome" T={T}>
                  {detailLoading ? (
                    <div style={{
                      padding: '12px 14px', background: T.surfaceHi, borderRadius: 10,
                      color: T.textDim, fontSize: 13,
                    }}>
                      Loading market options...
                    </div>
                  ) : outcomeOptions ? (
                    outcomeOptions.type === 'multi' ? (
                      // Multi-choice: one button per option
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {outcomeOptions.options.map(opt => {
                          const isSelected = form.option_id === opt.id;
                          return (
                            <button key={opt.id}
                              onClick={() => setForm(f => ({ ...f, outcome: opt.label, option_id: opt.id }))}
                              style={{
                                padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                                fontFamily: FONT_SANS, fontSize: 13, fontWeight: isSelected ? 600 : 400,
                                textAlign: 'left', transition: 'all 0.15s',
                                background: isSelected ? T.accentDim : T.surfaceHi,
                                border: `1px solid ${isSelected ? T.accent + '50' : T.border}`,
                                color: isSelected ? T.accent : T.textMuted,
                                display: 'flex', alignItems: 'center', gap: 10,
                              }}>
                              <div style={{
                                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                background: isSelected ? T.accent : T.textDim,
                                transition: 'background 0.15s',
                              }} />
                              {opt.label}
                              {isSelected && (
                                <span style={{ marginLeft: 'auto', fontSize: 11, color: T.accent }}>✓</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      // Binary: use actual yes_label / no_label from market
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        {[
                          { key: 'yes', label: outcomeOptions.yes },
                          { key: 'no',  label: outcomeOptions.no  },
                        ].map(o => {
                          const isSelected = form.outcome === o.key;
                          const color = o.key === 'yes' ? T.yes : T.no;
                          const dimColor = o.key === 'yes' ? T.yesDim : T.noDim;
                          return (
                            <button key={o.key}
                              onClick={() => setForm(f => ({ ...f, outcome: o.key, option_id: '' }))}
                              style={{
                                padding: '10px', borderRadius: 10, cursor: 'pointer',
                                fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600,
                                transition: 'all 0.15s',
                                background: isSelected ? dimColor : T.surfaceHi,
                                border: `1px solid ${isSelected ? color + '50' : T.border}`,
                                color: isSelected ? color : T.textMuted,
                              }}>
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <div style={{
                      padding: '12px 14px', background: T.surfaceHi, borderRadius: 10,
                      color: T.textDim, fontSize: 13,
                    }}>
                      Could not load market options — try selecting again
                    </div>
                  )}
                </FormField>
              )}

              {/* Amount + Duration side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Proposed Amount (KES)" T={T}>
                  <input type="number" min={10} value={form.proposed_amount}
                    onChange={e => setForm(f => ({ ...f, proposed_amount: e.target.value }))}
                    placeholder="e.g. 500"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: T.surfaceHi, border: `1px solid ${T.border}`,
                      borderRadius: 10, padding: '11px 14px',
                      color: T.text, fontFamily: FONT_MONO, fontSize: 13, outline: 'none',
                    }}
                  />
                </FormField>
                <FormField label="Vote Duration" T={T}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                    {[6, 12, 24, 48].map(h => (
                      <button key={h} onClick={() => setForm(f => ({ ...f, duration_hours: h }))} style={{
                        padding: '9px 0', borderRadius: 8, cursor: 'pointer',
                        fontFamily: FONT_SANS, fontSize: 12, transition: 'all 0.15s',
                        background: form.duration_hours === h ? T.accentDim : T.surfaceHi,
                        border: `1px solid ${form.duration_hours === h ? T.accent + '50' : T.border}`,
                        color: form.duration_hours === h ? T.accent : T.textMuted,
                        fontWeight: form.duration_hours === h ? 600 : 400,
                      }}>{h}h</button>
                    ))}
                  </div>
                </FormField>
              </div>

              <FormField label="Reasoning (optional)" T={T}>
                <textarea value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Why should the chama take this position?" rows={3}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: T.surfaceHi, border: `1px solid ${T.border}`,
                    borderRadius: 10, padding: '11px 14px',
                    color: T.text, fontFamily: FONT_SANS, fontSize: 13,
                    outline: 'none', resize: 'none', lineHeight: 1.6,
                  }}
                />
              </FormField>
            </div>

            {msg && (
              <div style={{
                marginTop: 14, borderRadius: 8, padding: '10px 14px',
                background: msg.type === 'success' ? T.yesDim : T.noDim,
                border: `1px solid ${msg.type === 'success' ? T.yes : T.no}30`,
                color: msg.type === 'success' ? T.yes : T.no, fontSize: 13,
              }}>
                {msg.text}
              </div>
            )}

            <button onClick={proposeVote} disabled={proposeLoading} style={{
              width: '100%', padding: '13px', borderRadius: 10, border: 'none', marginTop: 18,
              background: T.accent, color: '#fff', fontFamily: FONT_SANS,
              fontSize: 14, fontWeight: 600,
              cursor: proposeLoading ? 'not-allowed' : 'pointer',
              opacity: proposeLoading ? 0.6 : 1,
            }}>
              {proposeLoading ? 'Proposing...' : 'Submit Vote Proposal'}
            </button>
          </div>
        )}

        {/* Status tabs */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
          background: T.border, borderRadius: 10, overflow: 'hidden', marginBottom: 20,
        }}>
          {['pending', 'approved', 'rejected'].map(s => (
            <button key={s} onClick={() => setTab(s)} style={{
              padding: '11px', background: tab === s ? T.surfaceHi : T.surface,
              border: 'none', cursor: 'pointer', fontFamily: FONT_SANS, fontSize: 13,
              fontWeight: tab === s ? 600 : 400,
              color: tab === s ? (s === 'approved' ? T.yes : s === 'rejected' ? T.no : T.accent) : T.textMuted,
              borderBottom: tab === s ? `2px solid ${s === 'approved' ? T.yes : s === 'rejected' ? T.no : T.accent}` : '2px solid transparent',
              transition: 'all 0.15s', textTransform: 'capitalize',
            }}>{s}</button>
          ))}
        </div>

        {/* Vote cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ ...card, padding: 32, textAlign: 'center', color: T.textDim, fontSize: 14 }}>Loading...</div>
          ) : votes.length === 0 ? (
            <div style={{ ...card, padding: 32, textAlign: 'center', color: T.textDim, fontSize: 14 }}>No {tab} votes</div>
          ) : votes.map(v => (
            <VoteCard key={v.id} vote={v} onCast={castVote} isPending={tab === 'pending'} T={T} />
          ))}
        </div>
      </div>
    </div>
  );
}

function VoteCard({ vote, onCast, isPending, T }) {
  const totalWeight = parseFloat(vote.yes_weight || 0) + parseFloat(vote.no_weight || 0);
  const yesPct = totalWeight > 0 ? (parseFloat(vote.yes_weight || 0) / totalWeight) * 100 : 50;
  const hoursLeft = Math.max(0, Math.floor((new Date(vote.expires_at) - new Date()) / (1000 * 60 * 60)));
  const hasVoted = !!vote.my_vote;

  // outcome color — neutral accent since outcome could be a custom label
  const isYesOutcome = vote.outcome?.toLowerCase() === 'yes';
  const isNoOutcome = vote.outcome?.toLowerCase() === 'no';
  const outcomeColor = isYesOutcome ? T.yes : isNoOutcome ? T.no : T.accent;

  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: '20px', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: 3,
        background: outcomeColor, borderRadius: '12px 0 0 12px',
      }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: T.textDim, fontSize: 11, marginBottom: 4 }}>by {vote.proposed_by_username}</div>
          <div style={{ color: T.text, fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{vote.market_title}</div>
          {vote.description && (
            <div style={{ color: T.textMuted, fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>{vote.description}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <div style={{
            color: outcomeColor, fontSize: 11, fontWeight: 700,
            background: `${outcomeColor}14`, border: `1px solid ${outcomeColor}28`,
            borderRadius: 5, padding: '3px 9px',
          }}>
            {vote.outcome}
          </div>
          <div style={{ color: T.textDim, fontSize: 11, fontFamily: FONT_MONO }}>
            KES {parseFloat(vote.proposed_amount).toLocaleString()}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ color: T.yes, fontSize: 11, fontFamily: FONT_MONO }}>FOR {yesPct.toFixed(0)}%</span>
          <span style={{ color: T.no, fontSize: 11, fontFamily: FONT_MONO }}>AGAINST {(100 - yesPct).toFixed(0)}%</span>
        </div>
        <div style={{ height: 4, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 2, background: T.yes,
            width: `${yesPct}%`, transition: 'width 0.6s cubic-bezier(0.22,1,0.36,1)',
          }} />
        </div>
        <div style={{ color: T.textDim, fontSize: 11, marginTop: 4 }}>
          {vote.vote_count} vote{vote.vote_count !== 1 ? 's' : ''} cast
          {isPending && ` · ${hoursLeft}h remaining`}
        </div>
      </div>

      {isPending && !hasVoted && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { key: 'yes', label: 'Approve', color: T.yes, dim: T.yesDim },
            { key: 'no',  label: 'Reject',  color: T.no,  dim: T.noDim  },
          ].map(v => (
            <button key={v.key} onClick={() => onCast(vote.id, v.key)} style={{
              padding: '10px', borderRadius: 9, cursor: 'pointer',
              fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600,
              transition: 'opacity 0.2s',
              background: v.dim,
              border: `1px solid ${v.color}40`,
              color: v.color,
            }}>{v.label}</button>
          ))}
        </div>
      )}

      {hasVoted && (
        <div style={{
          background: T.surfaceHi, border: `1px solid ${T.border}`,
          borderRadius: 8, padding: '9px 14px', color: T.textMuted, fontSize: 12, fontWeight: 500,
        }}>
          You voted {vote.my_vote === 'yes' ? 'to approve' : 'to reject'}
        </div>
      )}
    </div>
  );
}

function FormField({ label, children, T }) {
  return (
    <div>
      <label style={{
        display: 'block', color: T.textDim, fontSize: 10, fontWeight: 500,
        letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6, fontFamily: FONT_SANS,
      }}>{label}</label>
      {children}
    </div>
  );
}